<template>
  <ATooltip
    v-if="currentPhoto && !failedToLoadImage"
    :destroy-tooltip-on-hide="true"
    :placement="tooltipPlacement"
  >
    <template v-if="tooltipText.length" #title>
      {{ tooltipText }}
    </template>
    <img
      v-show="imageLoaded"
      :src="currentPhoto"
      alt="avatar"
      :class="`${$style['user-image']} ${$style['user-image-' + size]}`"
      @load="() => (imageLoaded = true)"
      @error="() => (failedToLoadImage = true)"
    />
    <div v-if="!imageLoaded" class="placeholder-glow">
      <span
        class="placeholder bg-primary border border-secondary border-2"
        :class="`${$style['user-image']} ${$style['user-image-' + size]}`"
      />
    </div>
  </ATooltip>

  <BaseLeadStatusBadge
    v-else-if="user"
    :color="user.color ?? undefined"
    :label="userName"
    :square="false"
    :border="true"
    :size="size"
    :tooltip-text="tooltipText"
  />
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue'
import { toInitials } from '@/helpers/string.helper'
import { BadgeSize, LeadStatusColor, MissingNamePlaceholder } from '@/shared'
import { useI18n } from 'vue-i18n'
import { asyncComponentWrapper } from '@/composables/useReloadModal'
import { TooltipPlacement } from 'ant-design-vue/es/tooltip/abstractTooltipProps'

const ATooltip = asyncComponentWrapper(() => import('ant-design-vue/es/tooltip'))

const props = defineProps({
  user: {
    type: [Object, null] as PropType<{
      first_name: string
      last_name: string
      photo?: { url: string | null } | null
      color?: LeadStatusColor | null
    } | null>,
    required: true,
  },
  size: {
    type: String as PropType<BadgeSize>,
    default: 'md',
    required: false,
  },
  tooltipPlacement: {
    type: String as PropType<TooltipPlacement>,
    default: undefined,
    required: false,
  },
})

const { t } = useI18n()
const imageLoaded = ref<boolean>(false)
const failedToLoadImage = ref<boolean>(false)

const currentPhoto = computed((): string | null => {
  if (props.user?.photo) {
    return props.user.photo.url
  }

  return null
})

const userName = computed(() =>
  toInitials(props.user?.first_name ?? '', props.user?.last_name ?? ''),
)
const tooltipText = computed(() => {
  if (!props.user?.first_name && !props.user?.last_name) {
    return t(MissingNamePlaceholder)
  }

  return `${props.user?.first_name ?? ''} ${props.user?.last_name ?? ''}`
})
</script>

<style lang="scss" module>
.user-image {
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  object-fit: cover;

  > span {
    font-size: 14px;
    font-weight: bold;
  }
}

.user-image-lg {
  width: 2.5rem;
  height: 2.5rem;
}

.user-image-sm {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  min-height: 1.5rem;

  > span {
    font-size: 0.625rem;
  }
}

.user-image-xs {
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  min-height: 1rem;

  > span {
    font-size: 0.5rem;
  }
}
</style>
